import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Login = () => {
  const backgroundLogos = useMemo(() => {
    const logos = [];
    const rows = 10;
    const cols = 10;
    const centerRow = 5;
    const centerCol = 5;
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const distanceFromCenter = Math.sqrt(
          Math.pow(row - centerRow, 2) + Math.pow(col - centerCol, 2)
        );
        
        // Merkezden uzaklaştıkça küçül (derinlik efekti)
        const sizeBase = 130 - (distanceFromCenter * 12);
        const size = Math.max(60, Math.min(130, sizeBase));
        
        // Merkezden uzaklaştıkça opacity artır - %20 daha fazla görünür
        const opacityBase = 0.30 + (distanceFromCenter * 0.02);
        const opacity = Math.min(0.40, opacityBase).toFixed(2);
        
        // Hafif rotasyon varyasyonu
        const rotateVariation = [-15, -10, -5, 0, 5, 10, 15];
        const rotate = rotateVariation[(row + col) % rotateVariation.length];
        
        logos.push({
          id: row * cols + col,
          size: Math.round(size),
          top: (row * 10) + 1,
          left: (col * 10) + 1,
          rotate: rotate,
          opacity: opacity
        });
      }
    }
    return logos;
  }, []);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [branchId, setBranchId] = useState('');
  const [remember, setRemember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingUser, setCheckingUser] = useState(false);

  const grayThemes = [
    { id: 'beyaz', label: 'Beyaz', bg: '210 20% 98%', preview: '#fafbfc' },
    { id: 'acik', label: 'Açık Gri', bg: '210 15% 95%', preview: '#eff1f3' },
    { id: 'orta', label: 'Orta Gri', bg: '210 12% 92%', preview: '#e5e8eb' },
    { id: 'koyu', label: 'Koyu Gri', bg: '210 10% 89%', preview: '#dde0e4' },
  ];

  const [selectedTheme, setSelectedTheme] = useState(() => {
    return localStorage.getItem('fortis_bg_theme') || 'acik';
  });

  useEffect(() => {
    const theme = grayThemes.find(t => t.id === selectedTheme);
    if (theme) {
      document.documentElement.style.setProperty('--background', theme.bg);
      localStorage.setItem('fortis_bg_theme', selectedTheme);
    }
  }, [selectedTheme]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await axios.get(`${API}/branches`);
        setBranches(response.data);
      } catch (error) {
        console.error('Şubeler yüklenemedi:', error);
      }
    };
    loadBranches();
  }, []);

  // Check if user is super admin when username changes
  useEffect(() => {
    const checkSuperAdmin = async () => {
      if (!username || username.length < 3) {
        setIsSuperAdmin(false);
        return;
      }
      
      setCheckingUser(true);
      try {
        const response = await axios.post(`${API}/auth/check-super-admin`, { username });
        setIsSuperAdmin(response.data.is_super_admin);
        // Super admin can still select a branch, don't set placeholder
      } catch (error) {
        setIsSuperAdmin(false);
      }
      setCheckingUser(false);
    };
    
    // Debounce the check
    const timeoutId = setTimeout(checkSuperAdmin, 500);
    return () => clearTimeout(timeoutId);
  }, [username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Branch selection is required (except for super admin)
    if (!branchId && !isSuperAdmin) {
      toast.error('Lütfen şube seçiniz');
      return;
    }
    
    setIsLoading(true);
    // Super admin can optionally select a branch, normal users must select
    const finalBranchId = branchId || null;
    const result = await login(username, password, remember, finalBranchId);
    setIsLoading(false);
    if (result.success) {
      // Herkes dashboard'a yönlendirilir
      navigate('/dashboard/messages');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ backgroundColor: '#F1EDE4' }}>
      {/* Background pattern with 100 logos */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {backgroundLogos.map(logo => (
          <img 
            key={logo.id}
            src="https://customer-assets.emergentagent.com/job_independent-erp/artifacts/331mzum7_Fortis_logo_kurumsal_logo_amblem.png"
            className="absolute"
            style={{
              width: `${logo.size}px`,
              height: 'auto',
              top: `${logo.top}%`,
              left: `${logo.left}%`,
              transform: `rotate(${logo.rotate}deg)`,
              opacity: logo.opacity
            }}
            alt=""
          />
        ))}
      </div>

      <div className="w-full max-w-xl relative z-10">
        {/* Logo */}
        <div className="mb-1 flex items-center justify-center" data-testid="login-logo-container">
          <img 
            src="https://customer-assets.emergentagent.com/job_independent-erp/artifacts/prgex85y_18_01_2025_fortis_kurumsal_logolar.svg" 
            alt="FORTIS Logo" 
            className="h-[310px] w-auto"
            data-testid="login-logo"
          />
        </div>

        <Card data-testid="login-card" className="backdrop-blur-sm bg-white/95 shadow-xl p-2">
          <CardHeader className="space-y-1 pb-2">
            <CardTitle className="text-5xl font-bold text-center text-[#8B0000]" data-testid="login-title">
              E-CONFIRMATION
            </CardTitle>
            <CardDescription className="text-center font-bold uppercase text-lg" data-testid="login-description">
              FORTIS ERP SİSTEMİNE HOŞ GELDİNİZ
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3" data-testid="login-form">
              <div className="space-y-1">
                <Label htmlFor="username" className="text-lg font-medium" data-testid="login-username-label">Ad Soyad</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Ahmet Yılmaz"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="login-username-input"
                  disabled={isLoading}
                  className="h-14 text-lg"
                />
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="password" className="text-lg font-medium" data-testid="login-password-label">Şifre</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  disabled={isLoading}
                  className="h-14 text-lg"
                />
              </div>

              {/* Şube seçimi - super admin için isteğe bağlı, diğerleri için zorunlu */}
              <div className="space-y-1">
                <Label htmlFor="branch" className="text-lg font-medium" data-testid="login-branch-label">
                  Şube {!isSuperAdmin && <span className="text-red-500">*</span>}
                </Label>
                <select
                  id="branch"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  disabled={isLoading}
                  required={!isSuperAdmin}
                  data-testid="login-branch-select"
                  className="flex h-14 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-white px-4 py-2 text-lg shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">{isSuperAdmin ? 'Tüm Şubeler (varsayılan)' : 'Şube seçin'}</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </select>
                {isSuperAdmin && <p className="text-sm text-gray-500">Super admin olarak tüm şubelere erişebilirsiniz</p>}
              </div>
              
              <div className="flex items-center">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember" 
                    checked={remember}
                    onCheckedChange={setRemember}
                    data-testid="login-remember-checkbox"
                    disabled={isLoading}
                    className="h-6 w-6"
                  />
                  <Label 
                    htmlFor="remember" 
                    className="text-lg font-normal cursor-pointer"
                    data-testid="login-remember-label"
                  >
                    Beni Hatırla
                  </Label>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-14 text-xl font-semibold"
                data-testid="login-submit-button"
                disabled={isLoading}
              >
                {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </Button>
              

            </form>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="mt-4 text-center space-y-1" data-testid="login-footer">
          <p className="text-lg text-[hsl(var(--muted-foreground))]">
            Yazılımın Tüm Hakları Saklıdır FORTIS OTOMASYON
          </p>
          <p className="text-lg text-[hsl(var(--muted-foreground))]">
            Created by MEHMET ALİ İDİKURT
          </p>
          <p className="text-lg font-semibold text-[hsl(var(--brand-700))]">
            E-CONFIRMATION V.1.0.1002
          </p>
        </div>

        {/* Tema Seçici */}
        <div className="mt-4 flex items-center justify-center gap-3" data-testid="theme-selector">
          <span className="text-sm font-medium text-gray-500">Arka Plan:</span>
          {grayThemes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => setSelectedTheme(theme.id)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                selectedTheme === theme.id 
                  ? 'border-[#8B0000] scale-110 shadow-md' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              style={{ backgroundColor: theme.preview }}
              title={theme.label}
              data-testid={`theme-${theme.id}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Login;
